import { useCallback, useEffect, type FunctionComponent } from 'react';

import { formatContainers } from '@trello/atlassian-analytics';
import {
  isAdminOfOrganization,
  isMemberOfOrganization,
} from '@trello/business-logic/organization';
import { ErrorBoundary } from '@trello/error-boundaries';
import type { SentryErrorMetadata } from '@trello/error-reporting';
import { useFeatureGate } from '@trello/feature-gate-client';
import { WorkspaceIdProvider } from '@trello/id-context';
import { hasValidInviteTokenForModel } from '@trello/invitation-tokens';
import { RouteId, useIsActiveRoute } from '@trello/router';
import { navigate } from '@trello/router/navigate';
import { getOrganizationUrl } from '@trello/urls';

import { LazyError } from 'app/src/components/Error/LazyError';
import { RuntimeError } from 'app/src/components/Error/RuntimeError';
import { InvitationTokenPage } from 'app/src/components/InvitationLinkPage/InvitationTokenPage';
import { SettingsFullPage } from 'app/src/components/SettingsFullPage';
import { WorkspaceMembersView } from 'app/src/components/WorkspaceMembers/WorkspaceMembersView';
import type { WorkspacePageOrganizationQueryResult } from './useWorkspacePageOrganizationQuery';
import { useWorkspacePageOrganizationQuery } from './useWorkspacePageOrganizationQuery';
import { useWorkspacePageViewEvents } from './useWorkspacePageViewEvents';
import { WorkspacePage } from './WorkspacePage';
import { WorkspacePageSpinner } from './WorkspacePageSpinner';

interface WorkspaceMembersPageProps {
  orgNameOrId: string;
}

export type MemberTypeFilter = 'guest' | 'request' | 'team-member';

const errorBoundaryTags: SentryErrorMetadata['tags'] = {
  ownershipArea: 'trello-billing-platform',
  feature: 'Workspace Members Page',
};

export const WorkspaceMembersPage: FunctionComponent<
  WorkspaceMembersPageProps
> = ({ orgNameOrId }) => {
  const { startTask, taskSucceeded, taskAborted, taskFailed } =
    useWorkspacePageViewEvents({
      taskName: 'view-organization/members',
      source: 'workspaceMembersScreen',
    });

  useEffect(() => startTask(), [startTask]);

  const isCurrentOrganizationGuestRoute = useIsActiveRoute(
    RouteId.ORGANIZATION_GUESTS,
  );
  const isCurrentOrganizationRequestRoute = useIsActiveRoute(
    RouteId.ORGANIZATION_REQUESTS,
  );

  const memberTypeFilter: MemberTypeFilter = isCurrentOrganizationGuestRoute
    ? 'guest'
    : isCurrentOrganizationRequestRoute
      ? 'request'
      : 'team-member';

  const onOrganizationQueryCompleted = useCallback(
    (data: WorkspacePageOrganizationQueryResult) => {
      if (
        (isCurrentOrganizationRequestRoute &&
          !isAdminOfOrganization(data.member, data.organization)) ||
        !isMemberOfOrganization(
          data.member,
          data.organization,
          data.organization?.enterprise,
        )
      ) {
        taskAborted({
          containers: formatContainers({
            idOrganization: data.organization?.id,
          }),
          error: new Error('Redirected away from members page'),
        });
        return navigate(getOrganizationUrl(orgNameOrId), {
          trigger: true,
          replace: true,
        });
      }

      taskSucceeded({
        containers: formatContainers({ idOrganization: data.organization?.id }),
      });
    },
    [
      isCurrentOrganizationRequestRoute,
      orgNameOrId,
      taskSucceeded,
      taskAborted,
    ],
  );

  const onOrganizationQueryError = useCallback(
    (err: Error) => {
      // unauthorized, the fragment for the member or the requested organization failed
      // do not fail the task for unauthorized errors
      if (err.message === 'Failed to load organization') {
        taskAborted({ error: err });
      } else {
        taskFailed({ error: err });
      }
    },
    [taskAborted, taskFailed],
  );

  const {
    loading,
    member,
    organization,
    organizationId,
    isOrganizationAdmin,
    hasAccessToOrganization,
    error,
  } = useWorkspacePageOrganizationQuery({
    orgNameOrId,
    onCompleted: onOrganizationQueryCompleted,
    onError: onOrganizationQueryError,
  });

  const { value: isM2WorkspaceNavEnabled } = useFeatureGate(
    'billplat_m2_workspace_pages',
  );

  if (
    organization &&
    !hasAccessToOrganization &&
    !member &&
    hasValidInviteTokenForModel(organization)
  ) {
    return <InvitationTokenPage orgId={organization.id} />;
  }

  if (isM2WorkspaceNavEnabled) {
    return (
      <ErrorBoundary
        tags={errorBoundaryTags}
        errorHandlerComponent={RuntimeError}
      >
        <WorkspaceIdProvider value={organizationId}>
          <SettingsFullPage>
            {loading ? (
              <WorkspacePageSpinner />
            ) : error || !organization || !hasAccessToOrganization ? (
              <LazyError errorType={error ? 'serverError' : 'notFound'} />
            ) : (
              <WorkspaceMembersView
                workspaceId={organization.id}
                isOrganizationAdmin={isOrganizationAdmin}
                memberTypeFilter={memberTypeFilter}
              />
            )}
          </SettingsFullPage>
        </WorkspaceIdProvider>
      </ErrorBoundary>
    );
  }

  if (loading) {
    return <WorkspacePageSpinner />;
  }

  if (error || !organization || !hasAccessToOrganization) {
    return <LazyError errorType={error ? 'serverError' : 'notFound'} />;
  }

  return (
    <ErrorBoundary
      tags={errorBoundaryTags}
      errorHandlerComponent={RuntimeError}
    >
      <WorkspaceIdProvider value={organization.id}>
        <WorkspacePage
          orgId={organization.id}
          isOrgAdmin={isOrganizationAdmin}
          fullWidth
        >
          <WorkspaceMembersView
            workspaceId={organization.id}
            isOrganizationAdmin={isOrganizationAdmin}
            memberTypeFilter={memberTypeFilter}
          />
        </WorkspacePage>
      </WorkspaceIdProvider>
    </ErrorBoundary>
  );
};
